/* ═══════════════════════════════════════════════════════════════
   The Designer — the document tools of the row-2 toolbar.

   Everything here is mouse-first and visible (DESIGN §1): find &
   replace has a button, not just Ctrl+F; smart blocks insert from a
   SearchableSelect over the firm's catalog (§5 — every pick-list is
   a SearchableSelect); images and tables insert from plain buttons.

   R5: each control is its OWN toolbar item so the user can pin and
   unpin them individually (Tools ▸ Customise toolbar…). Each one
   subscribes to the editor itself rather than making the whole
   Designer re-render on every keystroke.

   Smart-block AUTHORING stays out of scope (contract §3.4): the
   picker only places existing catalog blocks, and the paper shows
   them resolved read-only through the same resolver the export uses.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import { Image as ImageIcon, Search, Table } from 'lucide-react';
import type { ProjectSpec, SmartBlock } from '../../shared/types';
import {
  BUILT_IN_SMART_BLOCKS,
  buildProjectContext,
  findSmartBlockInCatalog,
  resolveSmartToken,
} from '../../engine/smart-blocks';
import { SmartBlockService } from '../services/smartBlockService';
import { ProjectSpecService } from '../services/projectSpecService';
import { ProjectService } from '../services/projectService';
import { TeamService } from '../services/teamService';
import { OrgService } from '../services/orgService';
import { SearchableSelect } from '../components/SearchableSelect';
import { reportError } from '../services/errorLog';
import {
  insertImageBlock,
  insertSmartTokenBlock,
  insertTableBlock,
  pickImageFile,
} from './commands';
import { closeFind, getFindState, openFind } from './search';
import { FindReplace } from './FindReplace';
import { docToBlocks } from './schema';
import './tools.css';

/** Re-render this control (and only this control) on every editor
 *  transaction — plugin state such as "is find open" speaks through them. */
function useEditorTick(editor: Editor | null) {
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('transaction', force);
    return () => {
      editor.off('transaction', force);
    };
  }, [editor]);
}

/** Default params for a new instance: the schema's declared defaults under
 *  the block's own defaultParams — the same layering the resolver applies. */
function defaultParamsFor(sb: SmartBlock): Record<string, unknown> {
  const fromSchema = Object.fromEntries(
    (sb.paramSchema || [])
      .filter((p) => p.default !== undefined)
      .map((p) => [p.key, p.default as unknown])
  );
  return { ...fromSchema, ...(sb.defaultParams || {}) };
}

/**
 * Resolve a smart block to preview text with the SAME context shape the
 * export pipeline builds (mirrors BlockEditor's contextSources). Sources
 * that fail to load just leave their slice of context empty — the preview
 * degrades, the export re-resolves live either way.
 */
async function resolvePreviewText(
  sb: SmartBlock,
  spec: ProjectSpec,
  currentBlocks: ReturnType<typeof docToBlocks>,
  params: Record<string, unknown>
): Promise<string> {
  const [projects, allSpecs, assignments, allMembers, orgs] = await Promise.allSettled([
    ProjectService.list(),
    ProjectSpecService.listByProject(spec.projectId),
    TeamService.listAssignments(undefined, spec.projectId),
    TeamService.list(),
    OrgService.list(),
  ]);
  const projectList = projects.status === 'fulfilled' ? projects.value : [];
  const project = projectList.find((p) => p.id === spec.projectId) as
    | Record<string, unknown>
    | undefined;
  const memberById = new Map(
    (allMembers.status === 'fulfilled' ? allMembers.value : []).map((m) => [m.id, m])
  );
  const team = (assignments.status === 'fulfilled' ? assignments.value : [])
    .map((a) => {
      const m = memberById.get(a.teamMemberId) as Record<string, unknown> | undefined;
      if (!m) return null;
      return {
        id: m.id as string,
        name: (m.name || m.fullName || m.displayName) as string | undefined,
        email: m.email as string | undefined,
        role: (a.role || m.role) as string | undefined,
        title: m.title as string | undefined,
      };
    })
    .filter(Boolean) as Array<{
    id: string;
    name?: string;
    email?: string;
    role?: string;
    title?: string;
  }>;
  const orgList = orgs.status === 'fulfilled' ? orgs.value : [];
  const clientOrg = project?.clientId
    ? (orgList.find((o) => o.id === project.clientId) as Record<string, unknown> | undefined)
    : undefined;

  const context = buildProjectContext({
    project: {
      id: (project?.id as string) || spec.projectId,
      name: project?.name as string | undefined,
      number: project?.projectNumber as string | undefined,
      address: project?.address as string | undefined,
      phase: project?.currentPhaseId as string | undefined,
      clientProjectName: project?.clientProjectName,
      formaTokens: project?.formaTokens,
    },
    thisSpec: {
      id: spec.id,
      masterId: spec.masterId,
      csiNumber: spec.csiNumber,
      sectionNumber: spec.csiNumber,
      title: spec.title,
      coreContent: currentBlocks,
    },
    allProjectSpecs: allSpecs.status === 'fulfilled' ? allSpecs.value : [],
    team,
    client: clientOrg
      ? {
          id: clientOrg.id as string,
          name: clientOrg.name as string | undefined,
          address: clientOrg.address as string | undefined,
        }
      : undefined,
    currentIssuance: (spec.issuances || []).slice(-1)[0],
  });

  const tokenShape = {
    id: 'preview',
    level: 1,
    text: '',
    blockType: 'smart-token' as const,
    smartBlockId: sb.id,
    smartBlockKind: sb.kind,
    smartParams: params,
  };
  const resolved = resolveSmartToken(tokenShape, sb, context);
  return resolved
    .map((b) => b.text)
    .filter((t) => t)
    .join('\n');
}

/* ─── Find ─── */

export function FindButton({ editor }: { editor: Editor | null }) {
  // The button's pressed state tracks the plugin, whichever of Ctrl+F / the
  // button / Esc changed it.
  useEditorTick(editor);
  const findOpen = editor ? !!getFindState(editor.state)?.open : false;
  const toggleFind = () => {
    if (!editor) return;
    const dispatch = editor.view.dispatch.bind(editor.view);
    if (findOpen) {
      closeFind(editor.state, dispatch);
      editor.commands.focus();
    } else {
      openFind(editor.state, dispatch);
    }
  };
  return (
    <button
      type="button"
      className={`dsg-icon-btn${findOpen ? ' dsg-tool-on' : ''}`}
      onClick={toggleFind}
      disabled={!editor}
      aria-label="Find & replace (Ctrl+F)"
      aria-pressed={findOpen}
      title="Find & replace (Ctrl+F)"
    >
      <Search size={15} aria-hidden="true" />
    </button>
  );
}

/* ─── Insert image / table ─── */

export function InsertImageButton({
  editor,
  canEdit,
}: {
  editor: Editor | null;
  canEdit: boolean;
}) {
  const onInsertImage = async () => {
    if (!editor) return;
    const img = await pickImageFile();
    if (!img) return;
    insertImageBlock(editor.state, editor.view.dispatch.bind(editor.view), img.dataUrl, img.name);
    editor.commands.focus();
  };
  return (
    <button
      type="button"
      className="dsg-icon-btn"
      onClick={() => void onInsertImage()}
      disabled={!editor || !canEdit}
      aria-label="Insert image"
      title="Insert image after the current paragraph"
    >
      <ImageIcon size={15} aria-hidden="true" />
    </button>
  );
}

export function InsertTableButton({
  editor,
  canEdit,
}: {
  editor: Editor | null;
  canEdit: boolean;
}) {
  return (
    <button
      type="button"
      className="dsg-icon-btn"
      onClick={() => {
        if (!editor) return;
        insertTableBlock(editor.state, editor.view.dispatch.bind(editor.view));
        editor.commands.focus();
      }}
      disabled={!editor || !canEdit}
      aria-label="Insert table"
      title="Insert a 3×3 table after the current paragraph"
    >
      <Table size={15} aria-hidden="true" />
    </button>
  );
}

/* ─── Smart blocks ─── */

export function SmartBlockPicker({
  editor,
  canEdit,
  spec,
}: {
  editor: Editor | null;
  canEdit: boolean;
  spec: ProjectSpec;
}) {
  // ─── Smart-block catalog: built-ins + the firm's published blocks ───
  const [catalog, setCatalog] = React.useState<SmartBlock[]>([]);
  const refreshCatalog = React.useCallback(async () => {
    try {
      setCatalog(await SmartBlockService.list());
    } catch {
      setCatalog([]);
    }
  }, []);
  React.useEffect(() => {
    void refreshCatalog();
    const onSync = (ev: Event) => {
      const tables: string[] = (ev as CustomEvent).detail?.tables ?? [];
      if (tables.includes('smart_blocks')) void refreshCatalog();
    };
    window.addEventListener('specforma-sync-complete', onSync);
    return () => window.removeEventListener('specforma-sync-complete', onSync);
  }, [refreshCatalog]);

  const allBlocks = React.useMemo(() => [...BUILT_IN_SMART_BLOCKS, ...catalog], [catalog]);
  const options = React.useMemo(
    () =>
      allBlocks.map((sb) => ({
        value: sb.id,
        label: sb.name,
        hint: sb.kind === 'custom' ? 'Custom' : sb.kind.replace(/-/g, ' '),
        group: catalog.some((c) => c.id === sb.id) ? 'Firm smart blocks' : 'Built-in',
      })),
    [allBlocks, catalog]
  );

  const [inserting, setInserting] = React.useState(false);
  const insertSmartBlock = async (smartBlockId: string) => {
    if (!editor || !smartBlockId || inserting) return;
    const sb = findSmartBlockInCatalog(smartBlockId, catalog);
    if (!sb) return;
    setInserting(true);
    const params = defaultParamsFor(sb);
    let previewText = '';
    try {
      previewText = await resolvePreviewText(sb, spec, docToBlocks(editor.getJSON()), params);
    } catch (err) {
      reportError('Designer:smartBlockPreview', err);
    }
    setInserting(false);
    insertSmartTokenBlock(editor.state, editor.view.dispatch.bind(editor.view), {
      smartBlockId: sb.id,
      smartBlockKind: sb.kind,
      smartParams: params,
      previewText: previewText || sb.name,
    });
    editor.commands.focus();
  };

  return (
    <div
      className="dsg-tools-smart"
      title="Insert a smart block — it re-resolves from project data at every export"
    >
      <SearchableSelect
        value=""
        options={options}
        onChange={(v) => void insertSmartBlock(v)}
        placeholder={inserting ? 'Inserting…' : 'Smart block'}
        searchPlaceholder="Search smart blocks…"
        clearable={false}
        disabled={!editor || !canEdit || inserting}
        noResultsText="No smart blocks match"
      />
    </div>
  );
}

/* ─── The find & replace bar ───
   Hosted once, always — Ctrl+F must work whether or not the Find button is
   pinned to the toolbar. It hangs under whatever element it is anchored to. */

export function FindReplaceHost({
  editor,
  canEdit,
  anchorRef,
}: {
  editor: Editor | null;
  canEdit: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
}) {
  if (!editor) return null;
  return <FindReplace editor={editor} canEdit={canEdit} anchorRef={anchorRef} />;
}
